import G from "@/components/Grid.module.css";

import { MetricChart, PlotSizer } from "@/modules/compute/monitor/MetricChart.tsx";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import type { Translatable } from "@/modules/l10n/getTransText.ts";
import { MB, MB_P_S, MBIT_P_S } from "@/modules/l10n/units.tsx";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

function label(text: Translatable, unit: Translatable) {
    return (
        <>
            {text} ({unit})
        </>
    );
}

export function MetricsGrid({ vm }: { vm: Pick<VirtualMachineLoc, "uuid" | "status" | "location"> }) {
    const { t } = useLingui();
    const { status, uuid, location } = vm;
    const stopped = status === "stopped";

    return (
        <div className={G.WarrenGrid}>
            <GraphContainer label={label(t`CPU`, "%")} stopped={stopped}>
                <MetricChart host={uuid} metric="libvirt.guest_time_per_vcpu_delta" location={location} />
            </GraphContainer>

            <GraphContainer label={label(t`RAM`, MB)} stopped={stopped}>
                <MetricChart host={uuid} metric="libvirt.used_memory_kb" location={location} />
            </GraphContainer>

            <GraphContainer label={label(t`Disk I/O`, MB_P_S)} stopped={stopped}>
                <MetricChart host={uuid} metric="libvirt.block_wr_bytes_delta" location={location} />
            </GraphContainer>

            <GraphContainer label={label(t`Network bandwidth`, MBIT_P_S)} stopped={stopped}>
                <MetricChart host={uuid} metric="libvirt.net_rx_bytes_delta" location={location} />
            </GraphContainer>
        </div>
    );
}

function GraphContainer({ children, label, stopped }: { children: ReactNode; label: ReactNode; stopped: boolean }) {
    // NB: The graph must be stretched to fill the container for the auto-scaling to work
    return (
        <div className="flex flex-col gap-2 justify-stretch">
            <div>{label}</div>
            <div className={G.Chart}>
                {stopped ? <PlotSizer>{() => <div className={G.Stopped} />}</PlotSizer> : children}
            </div>
        </div>
    );
}
